import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import RowActionsMenu from './RowActionsMenu';
import './MandantenList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export interface MandantData {
  mandant_id: number;
  slug: string;
  firmenname: string;
  plan: 'STANDARD' | 'ENTERPRISE';
  status: 'AKTIV' | 'GESPERRT' | 'ARCHIVIERT';
  benutzer_anzahl: number;
  max_benutzer: number | null;
  adresse: string | null;
  ust_id: string | null;
  // Strukturierte Stammdaten fuer die E-Rechnung (M3 / AP 3.2)
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  land_code?: string | null;
  iban?: string | null;
  bic?: string | null;
  steuernummer?: string | null;
  email_rechnung?: string | null;
  primaerfarbe: string | null;
  erstellt_am: string;
}

interface AuthUser {
  name: string;
  email: string;
  rolle: string;
  tenant_id: number | null;
  tenant_slug: string | null;
  firmenname: string | null;
  primaerfarbe: string | null;
}

interface Props {
  authUser: AuthUser;
  onEdit: (mandant: MandantData) => void;
  refreshKey?: number;
}

const MandantenList: React.FC<Props> = ({ authUser, onEdit, refreshKey }) => {
  const [mandanten, setMandanten] = useState<MandantData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterText, setFilterText] = useState('');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` }), []);

  const loadMandanten = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/mandanten`, { headers });
      setMandanten(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Laden der Mandanten.');
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    loadMandanten();
  }, [loadMandanten, refreshKey]);

  const handleToggleStatus = async (m: MandantData) => {
    setActionLoading(m.mandant_id);
    try {
      const action = m.status === 'AKTIV' ? 'sperren' : 'aktivieren';
      await axios.put(`${API_URL}/api/mandanten/${m.mandant_id}/${action}`, {}, { headers });
      await loadMandanten();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Fehler beim Ändern des Status.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDemoData = async (m: MandantData) => {
    // Confirm-by-typing: Demo-Daten verändern den Datenbestand des Mandanten
    const eingabe = window.prompt(
      `Demo-Daten für "${m.firmenname}" anlegen?\n\n` +
      `Dies erstellt Beispieldaten im Mandanten und verändert dessen Datenbestand.\n` +
      `Zum Bestätigen den Firmennamen exakt eintippen:`
    );
    if (eingabe === null) return;
    if (eingabe.trim() !== m.firmenname) {
      alert('Eingabe stimmt nicht mit dem Firmennamen überein — abgebrochen.');
      return;
    }

    setActionLoading(m.mandant_id);
    try {
      await axios.post(`${API_URL}/api/mandanten/${m.mandant_id}/demo-daten`, {}, { headers });
      alert('Demo-Daten wurden erfolgreich angelegt.');
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Fehler beim Anlegen der Demo-Daten.');
    } finally {
      setActionLoading(null);
    }
  };

  const gefiltert = mandanten.filter(m => {
    if (!filterText) return true;
    const q = filterText.toLowerCase();
    return (
      m.firmenname.toLowerCase().includes(q) ||
      m.slug.toLowerCase().includes(q)
    );
  });

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'AKTIV':     return 'aktiv';
      case 'GESPERRT':  return 'gesperrt';
      case 'ARCHIVIERT': return 'archiviert';
      default:          return '';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'AKTIV':     return 'Aktiv';
      case 'GESPERRT':  return 'Gesperrt';
      case 'ARCHIVIERT': return 'Archiviert';
      default:          return status;
    }
  };

  return (
    <div className="mandanten-list-container">
      <div className="mandanten-list-header">
        <div>
          <h2 className="mandanten-list-title">Mandantenverwaltung</h2>
          <p className="mandanten-list-subtitle">
            {mandanten.filter(m => m.status === 'AKTIV').length} aktive Mandanten
          </p>
        </div>
      </div>

      <div className="mandanten-filter-row">
        <input
          type="text"
          placeholder="Firmenname oder Slug suchen..."
          value={filterText}
          onChange={e => setFilterText(e.target.value)}
          className="mandanten-search-input"
        />
      </div>

      {error && <div className="mandanten-error">{error}</div>}

      {loading ? (
        <div className="mandanten-loading">
          <div className="spinner" />
          <div>Mandanten werden geladen...</div>
        </div>
      ) : gefiltert.length === 0 ? (
        <div className="mandanten-empty">Keine Mandanten gefunden.</div>
      ) : (
        <div className="table-container">
        <table className="mandanten-table">
          <thead>
            <tr>
              <th>Slug</th>
              <th>Firmenname</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Benutzer</th>
              <th>Erstellt am</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {gefiltert.map(m => (
              <tr key={m.mandant_id}>
                <td className="mandant-slug">{m.slug}</td>
                <td className="mandant-firmenname">{m.firmenname}</td>
                <td>
                  <span className={`plan-badge ${m.plan === 'ENTERPRISE' ? 'enterprise' : ''}`}>
                    {m.plan}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${getStatusClass(m.status)}`}>
                    {getStatusLabel(m.status)}
                  </span>
                </td>
                <td className="mandant-benutzer">
                  {m.benutzer_anzahl}
                  {m.max_benutzer !== null ? ` / ${m.max_benutzer}` : ''}
                </td>
                <td className="mandant-datum">
                  {new Date(m.erstellt_am).toLocaleDateString('de-DE')}
                </td>
                <td className="mandanten-actions">
                  <button
                    className="btn-edit"
                    onClick={() => onEdit(m)}
                    disabled={actionLoading === m.mandant_id}
                  >
                    Bearbeiten
                  </button>
                  <RowActionsMenu
                    actions={[
                      {
                        label: m.status === 'AKTIV' ? 'Sperren' : 'Aktivieren',
                        onClick: () => handleToggleStatus(m),
                        disabled: actionLoading === m.mandant_id,
                        danger: m.status === 'AKTIV',
                      },
                      {
                        label: 'Demo-Daten einspielen',
                        onClick: () => handleDemoData(m),
                        disabled: actionLoading === m.mandant_id,
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default MandantenList;
